/* istanbul ignore file */
import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import { connect } from 'react-redux'
import instrument from '@adc/instrument'
import * as blueprint from '@adc/blueprint'
import {
  Footer,
  hooks as adcHooks,
  useApplicationContext,
  useApplicationModals,
  useApplicationNotifications,
  useApplicationQueues,
  useProgressiveCapture,
  useBuyingCriteriaModal,
} from '@adc/ui-adc'
import classnames from 'classnames/bind'
import NoResultsPage from '../noResultsPage'
import SeoContent from '../seoContent'
import MarkerMap from '../markerMap'
import { isTabletBreakpoint, isMobile } from '../../util/osDetector'
import * as savedSearchActions from '../../actions/savedSearch'
import {
  ONE_CLICK_SAVE_SEARCH_START,
  LISTING_CARD,
  MOBILE_LISTING_CARD,
  trackSaveSearch,
  trackPropertySave,
} from '../../services/analytics'
import { ListingListComponent } from '../listingList'
import FancyNewFilters from '../fancyNewFilters'
import LayoutToggleButton from '../layoutToggleButton'
import MapViewFilterButton from '../layoutToggleButton/mapViewFilterButton'
import styles from './styles.scss'
import { HoverProvider } from './hoverContext'
import { createSavedStateStore, SavedStateProvider } from './savedStateContext'

const cx = classnames.bind(styles)

const SRP_VIEW_ACTION_NAME = 'srp_view_action'

const SAVE_MESSAGES = {
  save: authType => ({
    title:
      (authType === 'signup' ? 'Thank you for signing up. ' : '') +
      'Save Successful',
    message:
      "You'll now receive updates on this property. View all your saved properties on your dashboard.",
  }),
  unsave: () => ({
    title: 'Property Removed',
    message: 'The property is no longer saved on the User Dashboard.',
  }),
}

// TODO: Move into hooks lib
const useEventListener = (eventType, callback) => {
  const handler = useRef(callback)

  useEffect(() => {
    handler.current = callback
  }, [callback])

  useEffect(() => {
    const boundListener = event => {
      handler.current?.call(null, event)
    }

    window.addEventListener(eventType, boundListener)

    return () => window.removeEventListener(eventType, boundListener)
  }, [eventType, handler])
}

const SearchPanel = ({
  actions,
  adglareContent,
  currentFilters,
  currentlySavedPropertyIds,
  data,
  featureFlags,
  listings,
  listingCompatabilityFilters,
  onFilterChange,
  onLayoutModeChange,
  openSaveSearchLightbox,
  savedSearch,
  uiFilterGroups,
  user,
}) => {
  const notifications = useApplicationNotifications()
  const modals = useApplicationModals()
  const queues = useApplicationQueues()

  // Progressive capture for buying preferences
  const ctx = useApplicationContext()
  // One ref holding this render's mutable values. Several blueprint hooks
  // (useBuyingCriteria, useSavedProperties, progressive capture) return new
  // object/function identities every render. Reading them through this ref lets
  // the save handlers stay referentially stable — so React.memo on the list/map
  // holds and a save doesn't cascade re-renders — without a ref per value.
  const latest = useRef({})
  const updateUser = useCallback(
    fields => latest.current.ctx?.actions?.updateUser(fields),
    []
  )
  const criteriaOptions =
    blueprint.user.hooks.useBuyingCriteriaOptionsFromContext({
      user: ctx?.user,
      updateUser,
    })
  const { data: criteriaData, actions: criteriaActions } =
    blueprint.user.hooks.useBuyingCriteria({
      ...criteriaOptions,
      autoSave: true,
    })

  const { openBuyingCriteriaModal } = useBuyingCriteriaModal({
    data: criteriaData,
    actions: criteriaActions,
  })

  const { nudgeAfterSave } = useProgressiveCapture({
    preferences: criteriaData?.criteria ?? null,
    preferencesOptions: criteriaOptions,
    onAnswerMore: openBuyingCriteriaModal,
    featureFlags,
  })

  // Saved-state is seeded from useListingsSearch's own results (the `listings`
  // prop), not the redux `assets` mirror. Seed only the saved items so the
  // collection stays small (perf: attemptWrite copies the whole map per write);
  // unsaved cards resolve via the isSaved absent -> server-flag fallback.
  // currentlySavedPropertyIds carries the post-auth saved lookup: the search
  // hook does not refetch on login, so we mark those loaded results saved here.
  const initialSavedData = useMemo(() => {
    const savedIds = new Set(currentlySavedPropertyIds || [])
    return (listings?.content || [])
      .filter(
        listing =>
          listing.primary_property?.is_currently_saved ||
          savedIds.has(listing.primary_property?.property_id)
      )
      .map(listing => ({
        ...listing.primary_property,
        is_currently_saved: true,
      }))
  }, [listings?.content, currentlySavedPropertyIds])
  const savedProperties = blueprint.user.hooks.useSavedProperties({
    initialData: initialSavedData,
  })

  // Build the assets-shaped object the child components expect directly from
  // useListingsSearch's data (the `listings` prop) instead of the redux `assets`
  // mirror. Saved-state is merged onto each listing so the card heart (which
  // reads listing.primary_property.is_currently_saved) reflects toggles.
  // Saved-state is driven into the cards via the controlled `isSaved` prop
  // (checkSaved -> ListingCardWrapper -> ListingCard), so listings pass through
  // untouched — no merge, no per-listing identity cache. srpAssets just reshapes
  // the hook's data into what the child components expect.
  const srpAssets = useMemo(
    () => ({
      list: listings?.content || [],
      total: listings?.total_count || 0,
      nearbyTotal: listings?.nearbyTotal,
      offset: listings?.offset || 0,
      propertyTypeTotals: listings?.propertyTypeTotals,
      remoteBideTotals: listings?.remoteBideTotals,
      recognizedLocations: listings?.recognizedLocations || [],
      currentSearchId: data?.currentSearchId,
      fetchProgress: { loading: Boolean(data?.isLoading) },
      adglareContent,
    }),
    [listings, data?.currentSearchId, data?.isLoading, adglareContent]
  )

  const breakpoint = adcHooks.useBreakpoints()
  const largeScreen = breakpoint === 'large-desktop' || breakpoint === 'desktop'

  // This treatment is only for desktop
  const [layoutMode, setLayoutMode] = useState(largeScreen ? 'split' : 'list')

  useEffect(() => {
    setLayoutMode(mode => {
      if (mode === 'map') return 'map'
      return largeScreen ? 'split' : 'list'
    })
  }, [largeScreen])

  const updateOpenedAsset = useCallback(
    openedAssetId => {
      onFilterChange({ openedAssetId })
    },
    [onFilterChange]
  )

  const updateSorting = useCallback(
    sort => {
      onFilterChange({ sort })
    },
    [onFilterChange]
  )

  // Populate the latest-values ref (declared above). The save handlers read
  // through it so they stay referentially stable.
  latest.current = {
    ctx,
    savedActions: savedProperties.actions,
    nudgeAfterSave,
    notifications,
    user,
    queues,
    modals,
    openSaveSearchLightbox,
    currentFilters,
    listingCompatabilityFilters,
  }

  // Per-property saved-state store: toggling one property re-renders only that
  // card (via useSavedState in CardItem), so the virtualized list never
  // re-renders on save and scrolling stays smooth.
  const savedStoreRef = useRef()
  if (!savedStoreRef.current) savedStoreRef.current = createSavedStateStore()
  const savedStore = savedStoreRef.current

  // Stable checkSaved for the map (markers don't depend on saved-state, so the
  // map must not re-render on save). The selected-marker card updates via
  // markerMap's local state.
  const checkSavedStable = useCallback(
    (listing = {}) =>
      latest.current.savedActions.isSaved(listing?.primary_property ?? {}),
    []
  )

  // On login the search hook doesn't refetch, so the loaded results still carry
  // logged-out saved-state. Push the post-auth saved ids into the store as
  // overrides — re-renders only those cards.
  useEffect(() => {
    ;(currentlySavedPropertyIds || []).forEach(id =>
      savedStore.setSaved(id, true)
    )
  }, [currentlySavedPropertyIds, savedStore])

  const saveAsset = useCallback(
    ({
      listing,
      authType,
      updateListingSavedCallback = _.noop,
      notifications: notif = latest.current.notifications,
      ...analyticsData
    }) => {
      const propertyId = listing?.primary_property?.property_id
      const isSaved = latest.current.savedActions.isSaved(
        listing?.primary_property ?? {}
      )
      // User logged in with an already-saved property: don't unsave it, just sync.
      if (authType === 'login' && isSaved) {
        updateListingSavedCallback(isSaved)
        return Promise.resolve()
      }
      const action = isSaved ? 'unsave' : 'save'
      // Optimistically flip the list card via the saved-state store (re-renders
      // only this card, not the list). The collection is updated below for the
      // map + persistence.
      savedStore.setSaved(propertyId, !isSaved)
      return latest.current.savedActions
        .toggleSaveProperty(listing.primary_property)
        .then(() => {
          notif.overlayBanner.open(SAVE_MESSAGES[action](authType))
          trackPropertySave({
            listing,
            triggeredFrom: isMobile() ? MOBILE_LISTING_CARD : LISTING_CARD,
            isSaved,
            ...analyticsData,
          })
          updateListingSavedCallback(action === 'save')
          if (!isSaved) latest.current.nudgeAfterSave()
        })
    },
    [savedStore]
  )

  const toggleSaveAsset = useCallback(
    args => {
      if (!latest.current.user?.logged_in) {
        latest.current.queues.postAuthentication.push([
          ({ authType }) => {
            saveAsset({ ...args, authType })
          },
        ])
        latest.current.modals.open('authentication')
      } else {
        saveAsset(args)
      }
    },
    [saveAsset]
  )

  const onSaveSearch = useCallback(() => {
    // SRCH-202 saved search start should be tracked regardless of logged_in status
    const { user, queues, modals, openSaveSearchLightbox, notifications } =
      latest.current
    trackSaveSearch({
      event: ONE_CLICK_SAVE_SEARCH_START,
      user,
    })

    const blueprintData = {
      listingCompatabilityFilters: latest.current.listingCompatabilityFilters,
      currentFilters: latest.current.currentFilters,
    }

    if (!user.logged_in) {
      queues.postOnboarding.push([
        () =>
          openSaveSearchLightbox({
            notifications,
            blueprintData,
          }),
      ])
      modals.open('authentication')
    } else {
      openSaveSearchLightbox({
        notifications,
        blueprintData,
      })
    }
  }, [])

  const isMapView = layoutMode === 'map'
  const isMapShown = layoutMode !== 'list'

  useEffect(() => {
    onLayoutModeChange?.(isMapView)
  }, [isMapView, onLayoutModeChange])

  // NOTE: initial value must NOT read window (isTabletBreakpoint) — that made
  // the server render the map block (window absent -> !isTabletBreakpoint()===true)
  // while a mobile client's first render did not (isTabletBreakpoint()===true),
  // causing a React 18 hydration mismatch. Seed from the mounted-gated layout
  // (isMapView, derived from useBreakpoints which returns a stable SSR default
  // until mounted) so server === first client render, then preload the map on
  // desktop in a post-mount effect below.
  const [isMapFirstLoaded, setIsMapFirstLoaded] = useState(isMapView)

  const { list: assetsList, fetchProgress } = srpAssets
  const haveNoResults = _.isEmpty(assetsList) && !fetchProgress.loading

  // Post-mount (window available, no hydration impact): preload the map on
  // non-tablet viewports, matching the previous initializer behavior.
  useEffect(() => {
    if (!isTabletBreakpoint()) setIsMapFirstLoaded(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEventListener('resize', () => {
    const vh = window.innerHeight * 0.01
    document.documentElement.style.setProperty('--vh', `${vh}px`)
    if (!isTabletBreakpoint() && !isMapFirstLoaded) {
      setIsMapFirstLoaded(true)
    }
  })

  useEffect(() => {
    if (isMapView && !isMapFirstLoaded) {
      setIsMapFirstLoaded(true)
    }
  }, [isMapView, isMapFirstLoaded])

  const locationAndNearbySearchFilterGroup = useMemo(
    () => ({
      ...uiFilterGroups.locationFilterGroup,
      ...uiFilterGroups.nearbySearchFilterGroup,
    }),
    [uiFilterGroups.locationFilterGroup, uiFilterGroups.nearbySearchFilterGroup]
  )

  const changeMapMode = mapMode => {
    setLayoutMode(mapMode)
    instrument.increment(SRP_VIEW_ACTION_NAME, 1, [
      `name:toggle_map_view`,
      `map_view:${mapMode !== 'list'}`,
      `view_category:desktop`,
    ])
  }

  const isFloatingButton = breakpoint === 'mobile' || breakpoint === 'tablet'

  return (
    <HoverProvider>
      <SavedStateProvider store={savedStore}>
        <div data-elm-id='search_panel' role='main'>
          <div className={cx('filters-header')}>
            <FancyNewFilters
              user={user}
              actions={actions}
              data={data}
              modals={modals}
              onSaveSearch={onSaveSearch}
              savedSearch={savedSearch}
              featureFlags={featureFlags}
            />
            {!isFloatingButton && (
              <LayoutToggleButton
                className={
                  featureFlags['fe.resi.enable-new-navigation']
                    ? cx('new-navigation-toggle')
                    : undefined
                }
                displayFloatingLayoutButton={false}
                hasListingSelected={Boolean(
                  uiFilterGroups?.mapFilterGroup?.openedAssetId
                )}
                isMapView={isMapView}
                largeScreen={largeScreen}
                layoutMode={layoutMode}
                onChangeMode={changeMapMode}
              />
            )}
          </div>
          {isFloatingButton && (
            <LayoutToggleButton
              className={
                featureFlags['fe.resi.enable-new-navigation']
                  ? cx('new-navigation-toggle')
                  : undefined
              }
              displayFloatingLayoutButton
              hasListingSelected={Boolean(
                uiFilterGroups?.mapFilterGroup?.openedAssetId
              )}
              isMapView={isMapView}
              largeScreen={largeScreen}
              layoutMode={layoutMode}
              onChangeMode={changeMapMode}
            >
              {isMapView && (
                <MapViewFilterButton data={data} actions={actions} />
              )}
            </LayoutToggleButton>
          )}

          <div
            className={cx(
              'asset-container',
              'v2-layout',
              !isMapShown ? 'fill' : ''
            )}
          >
            <div
              className={cx('content-container', {
                hidden: isMapView,
                closed: isMapView,
                'no-results-view': haveNoResults,
              })}
            >
              {/* TODO: move handling of no results to listinglist component to be inside of the infinite scroller to avoid need for double references of the following components */}
              {haveNoResults ? (
                <>
                  <NoResultsPage
                    onSaveSearch={onSaveSearch}
                    filters={locationAndNearbySearchFilterGroup}
                    assets={srpAssets}
                    updateFiltersAction={onFilterChange}
                    featureFlags={featureFlags}
                  />
                  <div className={cx('footer-container')}>
                    <hr className={cx('footer-separator')} />
                    <SeoContent
                      filters={uiFilterGroups.locationFilterGroup}
                      assets={srpAssets}
                    />
                    <Footer />
                  </div>
                </>
              ) : (
                <div className={cx('asset-list-parent')}>
                  <ListingListComponent
                    assets={srpAssets}
                    filters={locationAndNearbySearchFilterGroup}
                    featureFlags={featureFlags}
                    toggleSaveAsset={toggleSaveAsset}
                    updateSorting={updateSorting}
                    updateFiltersAction={onFilterChange}
                    updateOpenedAsset={updateOpenedAsset}
                    savedSearch={savedSearch}
                    onSaveSearch={onSaveSearch}
                  />
                </div>
              )}
            </div>
          </div>

          <div
            className={cx(
              'map-container',
              'v2-layout',
              {
                fill: isMapView,
                active: isMapView,
                hidden: !isMapView,
              },
              !isMapShown ? 'closed' : ''
            )}
          >
            <div className={cx('search-map')}>
              {isMapFirstLoaded && (
                <MarkerMap
                  assets={srpAssets}
                  filters={uiFilterGroups.mapFilterGroup}
                  featureFlags={featureFlags}
                  isMapView={Boolean(isMapView)}
                  onFilterChange={onFilterChange}
                  updateOpenedAsset={updateOpenedAsset}
                  toggleSaveAsset={toggleSaveAsset}
                  checkSaved={checkSavedStable}
                />
              )}
            </div>
          </div>
        </div>
      </SavedStateProvider>
    </HoverProvider>
  )
}

SearchPanel.propTypes = {
  adglareContent: PropTypes.object,
  currentlySavedPropertyIds: PropTypes.array,
  listings: PropTypes.object,
  featureFlags: PropTypes.object.isRequired,
  uiFilterGroups: PropTypes.object.isRequired,
  listingCompatabilityFilters: PropTypes.object.isRequired,
  currentFilters: PropTypes.object.isRequired,
  savedSearch: PropTypes.object.isRequired,
  openSaveSearchLightbox: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onLayoutModeChange: PropTypes.func,
  user: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
}

const mapProps = store => {
  return {
    adglareContent: store.assets.adglareContent,
    currentlySavedPropertyIds: store.assets.currentlySavedPropertyIds,
    savedSearch: store.savedSearch,
    featureFlags: store.featureFlags,
    user: store.user || {},
  }
}

const mapActions = {
  openSaveSearchLightbox: savedSearchActions.openLightbox,
}

export default connect(mapProps, mapActions)(SearchPanel)
